/**
 * Determines if two objects have the same keys.
 *
 * This function checks if both objects have the same number of keys and if every key in the first object
 * also exists in the second object. It does not compare the values of the keys, nor does it check for
 * key existence in the reverse direction (i.e., keys in objectB that are not in objectA).
 *
 * @param {Object} objectA - The first object to be compared.
 * @param {Object} objectB - The second object to be compared.
 * @returns {boolean} - Returns true if both objects have the same set of keys, false otherwise.
 */
export function objectsHasSameKeys(objectA, objectB) {
  return (
    Object.keys(objectA).length === Object.keys(objectB).length &&
    Object.keys(objectA).every((key) => objectB.hasOwnProperty(key))
  );
}

/**
 * returns a promise that resolves after the specified number of milliseconds.
 *
 * @param {number} ms - number of milliseconds to wait.
 * @returns {Promise<void>} promise that resolves after the specified delay.
 */
export function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function debounce(func, wait) {
  let timeout;

  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };

    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export function getIconSize(type) {
  let size;
  switch (type) {
    case "large":
      size = 1.25;
      break;
    case "medium":
      size = 1;
      break;
    case "small":
      size = 0.8;
      break;
    case "seaplane":
      size = 1;
      break;
    case "custom":
      size = 0.8;
      break;
    default:
      size = 1; // Default size if none of the types match
  }

  return size;
}

export function airportIdMap(airportsarray) {
  let idMap = new Map();
  for (const airport of airportsarray) {
    try {
      let airportObject = {
        name: airport.name,
        country: airport.country.name,
        country_iso: airport.country.iso,
      };

      if (airport.type && airport.type === "custom") {
        airportObject.type = "custom";
      }

      idMap.set(airport.airportId, airportObject);
    } catch (error) {
      console.log("Error while creating airportId map: ", error);
    }
  }

  return idMap;
}

// export function timeConverter(minutes) {
//   const time = { hours: Math.floor(minutes / 60), minutes: minutes % 60 };
//   return time;
// }

export function timeConverter(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  // Create an array and conditionally add hour or minute text if they are not zero
  const parts = [];
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);

  // Join the parts with a comma and space, handling cases where one of them might be zero
  return parts.join(", ");
}

/**
 * Function to modify the keys of an object based on a modification function.
 * @param {Object} obj - The original object whose keys are to be modified.
 * @param {Function} modifyFn - The function to modify each key.
 * @returns {Object} - A new object with modified keys.
 */
export function modifyKeys(obj, modifyFn) {
  // Use Object.keys to get an array of the object's own enumerable property keys
  return Object.keys(obj).reduce((acc, key) => {
    // Apply the modification function to the current key to get the modified key
    const modifiedKey = modifyFn(key);
    // Assign the value of the original key to the new modified key in the accumulator object
    acc[modifiedKey] = obj[key];
    // Return the accumulator for the next iteration
    return acc;
  }, {}); // Initialize the accumulator as an empty object
}

/**
 * Capitalizes the first letter of a given string.
 *
 * @param {string} string - The string to capitalize.
 * @returns {string} The input string with the first letter capitalized.
 */
export function capitalizeFirstLetter(string) {
  return string[0].toUpperCase() + string.slice(1);
}

/**
 * Calculates a zoom-based size multiplier for map markers.
 * This function scales the multiplier smoothly based on the zoom level
 * using a normalized range between a minimum and maximum zoom.
 *
 * @param {number} zoomLevel - The current zoom level of the map (typically between 3 and 22).
 * @returns {number} - The calculated zoom multiplier, where lower zoom levels return smaller values and higher zoom levels return larger values.
 */
export function calculateMultiplier(zoomLevel) {
  const minZoom = 3;
  const maxZoom = 22;

  // Normalize the zoom level between 0 and 1
  const zoomFactor = (zoomLevel - minZoom) / (maxZoom - minZoom);

  // Define the minimum and maximum size multipliers
  const minMultiplier = 0.3; // Minimum size (at zoom level 3)
  const maxMultiplier = 4; // Maximum size (at zoom level 22)

  // Return the scaled multiplier based on the zoom level
  return minMultiplier + zoomFactor * (maxMultiplier - minMultiplier);
}

/**
 * Calculates the geographic midpoint of a polyline path.
 *
 * @param {google.maps.MVCArray<google.maps.LatLng>} path - The path of the polyline as an array of LatLng points.
 * Each point should have a `lat()` and `lng()` method to get the latitude and longitude, respectively.
 *
 * @returns {{lat: number, lng: number}} The latitude and longitude of the midpoint as an object.
 */
export function calculatePolylineMidpoint(path) {
  let totalLat = 0;
  let totalLng = 0;

  path.forEach((point) => {
    totalLat += point.lat();
    totalLng += point.lng();
  });

  const midpointLat = totalLat / path.length;
  const midpointLng = totalLng / path.length;

  return { lat: midpointLat, lng: midpointLng };
}

/**
 * Smoothly centers the map on the polyline's midpoint and adjusts the zoom level based on the polyline's length.
 *
 * @param {google.maps.Polyline} polyline - The polyline to highlight.
 * @param {google.maps.Map} map - The Google Map instance.
 */
export function centerMapOnPolyline(polyline, map) {
  if (map && polyline) {
    const path = polyline.getPath();

    // Calculate the length of the polyline in meters
    const lengthInMeters = google.maps.geometry.spherical.computeLength(path);
    const lengthInKm = lengthInMeters / 1000;

    // Compute the appropriate zoom level
    const zoomLevel = calculateZoomLevel(lengthInKm);

    // Get the midpoint of the polyline
    const midpoint = calculatePolylineMidpoint(path.getArray());

    // Smoothly pan to the midpoint and set the zoom level
    map.panTo(midpoint);
    map.setZoom(zoomLevel);
  }
}

/**
 * Calculates an appropriate zoom level based on the length of the polyline.
 *
 * @param {number} lengthInKm - The length of the polyline in kilometers.
 * @returns {number} - The calculated zoom level.
 */
function calculateZoomLevel(lengthInKm) {
  const maxZoomLevel = 15; // Closest zoom
  const minZoomLevel = 7; // Farthest zoom

  // Clamp the length to avoid extreme zoom levels
  const clampedLength = Math.max(1, Math.min(lengthInKm, 400));

  // Use a logarithmic scale for a smoother zoom transition
  const zoomLevel =
    maxZoomLevel -
    ((Math.log(clampedLength) - Math.log(1)) / (Math.log(400) - Math.log(1))) *
      (maxZoomLevel - minZoomLevel);

  return Math.round(zoomLevel);
}

/**
 * Smoothly centers the map on a single point and adjusts the zoom level.
 *
 * @param {google.maps.Map} map - The Google Map instance.
 * @param {google.maps.LatLng | {lat: number, lng: number}} coordinates - The coordinates to center the map on.
 * @param {number} [zoomLevel=6] - The zoom level to set when centering the map.
 */
export function centerMapOnPoint(map, coordinates, zoomLevel = 10) {
  if (map && coordinates) {
    // Center the map on the provided coordinates
    map.panTo(coordinates);

    // Set the specified zoom level
    map.setZoom(zoomLevel);
  }
}

/**
 * Creates a map of airports with their route counts initialized to 0.
 *
 * @param {Array<Object>} filteredAirports - The list of filtered airport objects.
 * Each airport object should contain a valid `airportId` property.
 * @returns {Object} A map where each key is an airport ID, and the value is initialized to 0.
 *
 * @throws Will log an error if `filteredAirports` is not an array.
 * @throws Will log a warning if an airport object lacks a valid `airportId`.
 */
export function createAirportRouteCountMap(filteredAirports) {
  const airportRouteMap = {};

  // Check if filteredAirports is a valid array
  if (!Array.isArray(filteredAirports)) {
    console.error("Invalid input: 'filteredAirports' should be an array.");
    return airportRouteMap;
  }

  filteredAirports.forEach((airport) => {
    // Ensure airport has a valid airportId
    if (airport && typeof airport.airportId === "string") {
      airportRouteMap[airport.airportId] = 0;
    } else {
      console.warn(
        "Skipped an airport with invalid or missing 'airportId':",
        airport,
      );
    }
  });

  return airportRouteMap;
}

// /**
//  * Calculates the total CO₂ emissions and per-passenger CO₂ emissions
//  * for a given short-haul flight using a phase-based model.
//  * Supports both the conventional ATR 42-600 and the hybrid-electric ES-30,
//  * which can fly up to 200 km on battery during climb/cruise/descent
//  * and always uses battery for ground operations (taxi, take-off, landing).
//  *
//  * @param {number} distance_km - Total flight distance in kilometers.
//  * @param {number} [load_factor=0.8] - Proportion of seats occupied (default 80%).
//  * @param {number} [seats=48] - Total number of seats in the aircraft (default 48).
//  * @param {string} [type="ATR42"] - Aircraft type:
//  *    - `"ATR42"` for the conventional turboprop.
//  *    - `"ES30"` for the hybrid-electric aircraft.
//  * @returns {{ totalCO2_kg: number, CO2_perPassenger: number }}
//  *   - totalCO2_kg: Total kilograms of CO₂ emitted.
//  *   - CO2_perPassenger: Kilograms of CO₂ emitted per passenger.
//  */
// export function calculateCO2(
//   distance_km,
//   load_factor = 0.8,
//   seats = 48,
//   type = "ATR42"
// ) {
//   // 1. Compute number of passengers actually on board
//   const pax = load_factor * seats;

//   // 2. Emission factor: each kg of fuel burned produces this many kg of CO₂
//   const emission_factor = 3.16; // kg CO₂ per kg fuel

//   // --- Ground operations fuel (all-electric on ES-30) ---
//   const taxiFuel = 16; // kg fuel equivalent for taxi
//   const takeoffFuel = 30; // kg fuel equivalent for takeoff roll
//   const landingFuel = 18; // kg fuel equivalent for landing roll

//   // --- Climb phase parameters ---
//   const climbRate = 12; // kg fuel burned per minute of climb
//   const climbTime = 10; // minutes spent climbing
//   const climbKmNom = 50 * 1.852; // nominal climb distance: 50 NM → km

//   // --- Descent phase parameters ---
//   const descentRate = 0.8; // kg fuel burned per nautical mile of descent
//   const descentKmNom = 70 * 1.852; // nominal descent distance: 70 NM → km

//   // --- Cruise phase parameter ---
//   const cruiseRate = 0.648; // kg fuel burned per km in cruise

//   // ES-30’s all-electric flight-phase range (km)
//   const battRangeKm = 200;

//   // 3. Split the total distance into climb, cruise, and descent segments
//   const totalPhaseKm = climbKmNom + descentKmNom;
//   let climbKm, descentKm, cruiseKm;

//   if (distance_km <= totalPhaseKm) {
//     // Very short flight: scale climb and descent proportionally, no cruise
//     const frac = distance_km / totalPhaseKm;
//     climbKm = climbKmNom * frac;
//     descentKm = descentKmNom * frac;
//     cruiseKm = 0;
//   } else {
//     // Normal operation: full climb and descent, remainder is cruise
//     climbKm = climbKmNom;
//     descentKm = descentKmNom;
//     cruiseKm = distance_km - totalPhaseKm;
//   }

//   // 4. Compute raw fuel burn for each phase (assuming purely combustion)
//   let climbFuel = climbTime * climbRate * (climbKm / climbKmNom);
//   let descentFuel = (descentKm / 1.852) * descentRate;
//   let cruiseFuel = cruiseKm * cruiseRate;

//   // 5. Assign ground-phase fuel
//   let fuelTaxi = taxiFuel;
//   let fuelTO = takeoffFuel;
//   let fuelLanding = landingFuel;

//   // 6. If the aircraft is the hybrid ES-30, adjust for battery range
//   if (type === "ES30") {
//     // a) Electric ground operations: zero out taxi, takeoff, landing fuel
//     fuelTaxi = 0;
//     fuelTO = 0;
//     fuelLanding = 0;

//     // b) Allocate remaining 200 km of battery power across
//     //    descent → cruise → climb (last-leg first logic)
//     let battLeft = battRangeKm;

//     // b1) Descent: use battery for as much of descent as possible
//     const useBattDescentKm = Math.min(descentKm, battLeft);
//     if (descentKm > 0) {
//       // reduce combustion burn in proportion to battery‐driven km
//       descentFuel *= 1 - useBattDescentKm / descentKm;
//     }
//     battLeft -= useBattDescentKm;

//     // b2) Cruise: battery covers next chunk up to remaining capacity
//     const useBattCruiseKm = Math.min(cruiseKm, battLeft);
//     cruiseFuel -= useBattCruiseKm * cruiseRate;
//     battLeft -= useBattCruiseKm;

//     // b3) Climb: final application of leftover battery capacity
//     const useBattClimbKm = Math.min(climbKm, battLeft);
//     if (climbKm > 0) {
//       climbFuel *= 1 - useBattClimbKm / climbKm;
//     }
//     battLeft -= useBattClimbKm;
//   }

//   // 7. Sum all fuel burned (kg)
//   const totalFuelKg =
//     fuelTaxi + fuelTO + climbFuel + cruiseFuel + descentFuel + fuelLanding;

//   // 8. Convert to CO₂ emissions and per-passenger values
//   const totalCO2_kg = totalFuelKg * emission_factor;
//   const CO2_perPassenger = totalCO2_kg / pax;

//   return {
//     totalCO2_kg,
//     CO2_perPassenger,
//   };
// }

/**
 * Calculates the total CO₂ emissions and per-passenger CO₂ emissions
 * using a fixed SSB factor (195 g CO₂ per passenger-km).
 *
 * Assumptions:
 * - Baseline factor: 0.195 kg CO₂ per passenger-km.
 * - ES-30 emits 0 for the first 200 km; only distance beyond 200 km counts.
 * - Non-ES30 types emit for the full distance.
 *
 * @param {number} distance_km - Total flight distance in kilometers.
 * @param {number} [load_factor=0.8] - Proportion of seats occupied (default 80%).
 * @param {number} [seats=48] - Total number of seats in the aircraft (default 48).
 * @param {string} [type="ATR42"] - "ATR42" or "ES30".
 * @returns {{ totalCO2_kg: number, CO2_perPassenger: number }}
 *   - totalCO2_kg: Total kilograms of CO₂ emitted (all passengers).
 *   - CO2_perPassenger: Kilograms of CO₂ emitted per passenger.
 */
export function calculateCO2(
  distance_km,
  load_factor = 0.8,
  seats = 48,
  type = "ATR42",
) {
  const loadFactorOverride = 1; // Temp fix, forcing 100% load factor
  const SSB_CO2_PER_PAX_KM_KG = 0.195; // kg CO₂ per passenger-km

  // Normalize inputs
  const d = Math.max(0, Number(distance_km) || 0);
  // const pax = Math.max(0, Number(load_factor) * Number(seats));
  const pax = Math.max(0, Number(loadFactorOverride) * Number(seats)); // Temp fix, forcing 100% load factor
  // ES-30 saves the first 200 km; others don't
  const effectiveKm = type === "ES30" ? Math.max(0, d - 200) : d;

  const CO2_perPassenger = SSB_CO2_PER_PAX_KM_KG * effectiveKm; // kg
  const totalCO2_kg = CO2_perPassenger * pax;

  return {
    totalCO2_kg,
    CO2_perPassenger,
  };
}

// /**
//  * Calculates total and per-passenger CO₂ using a fixed SSB emissions factor.
//  *
//  * Assumptions:
//  * - SSB factor: 195 g CO₂ per passenger-km (0.195 kg/pkm).
//  * - ES-30 emits 0 for the first 200 km (battery), then 0.195 kg/pkm beyond.
//  * - ATR42 (or any non-ES30 type) uses 0.195 kg/pkm for the full distance.
//  *
//  * @param {number} distance_km - Flight distance in kilometers.
//  * @param {number} passengers  - Number of passengers carried (e.g., actual or yearly).
//  * @param {string} [type="ATR42"] - "ATR42" or "ES30".
//  * @returns {{ totalCO2_kg: number, CO2_perPassenger: number }}
//  *   - totalCO2_kg: Total kilograms of CO₂ emitted (for all passengers).
//  *   - CO2_perPassenger: Kilograms of CO₂ per passenger for this distance.
//  */
// export function calculateCO2(distance_km, passengers, type = "ATR42") {
//   const SSB_CO2_PER_PAX_KM_KG = 0.195; // kg CO₂ per passenger-km

//   const d = Number(distance_km) > 0 ? Number(distance_km) : 0;
//   const pax = Number(passengers) > 0 ? Number(passengers) : 0;

//   // ES-30: first 200 km are zero-emission, remainder uses SSB factor.
//   const effectiveKm = type === "ES30" ? Math.max(0, d - 200) : d;

//   const CO2_perPassenger = SSB_CO2_PER_PAX_KM_KG * effectiveKm; // kg
//   const totalCO2_kg = CO2_perPassenger * pax;

//   return { totalCO2_kg, CO2_perPassenger };
// }

/**
 * Rounds a numeric value to a specified number of decimal places
 * using true mathematical rounding (half up).
 *
 * @param {number} value - The numeric value to round.
 * @param {number} decimals - The number of decimal places to retain.
 *                            Must be a non-negative integer.
 * @returns {number} The value rounded to `decimals` places.
 *
 * @example
 * // Round to one decimal place
 * roundDecimals(730.1966018718502, 1); // → 730.2
 *
 * @example
 * // Round to two decimal places
 * roundDecimals(15.215, 2); // → 15.22
 */
export function roundDecimals(value, decimals) {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
}

/**
 * Formats a number with up to two decimal places and appends
 * a suffix for thousands (K), millions (M), or billions (B).
 *
 * Examples:
 *   formatNumber(2345678900) // "2.35B"
 *   formatNumber(1236000)    // "1.24M"
 *   formatNumber(14563)      // "14.56K"
 *   formatNumber(999)        // "999"
 *
 * @param {number|string} num - The input number to format. Can be a number or numeric string.
 * @returns {string} The formatted number with an appropriate suffix.
 */
export function formatNumber(num) {
  const n = Number(num);
  if (n >= 1e9) {
    // e.g. 2 345 678 900 → "2.35B"
    return (n / 1e9).toFixed(2).replace(/\.00$/, "") + "B";
  }
  if (n >= 1e6) {
    // e.g. 1 236 000 → "1.24M"
    return (n / 1e6).toFixed(2).replace(/\.00$/, "") + "M";
  }
  if (n >= 1e3) {
    // e.g. 14 563 → "14.56K"
    return (n / 1e3).toFixed(2).replace(/\.00$/, "") + "K";
  }
  // under 1 000, just the integer
  return Math.floor(n).toString();
}
